import { Dispatch, SetStateAction } from 'react';

import { Option } from 'types';

import { colors } from 'themes/config';

import * as S from './Select.styles';

// same gray as the other hover states (e.g. the budget row menu)
const OPTION_HOVER = '#f0f0f0';

type ValueType = string | number | boolean | { value: number; label: number };

type SelectProps = {
  options: Option[];
  label?: string;
  defaultValue?: Option;
  selectedValue?: ValueType;
  onChange: Dispatch<SetStateAction<any>>; // eslint-disable-line
  hasMaxMenuHeight?: boolean;
  onMenuOpen?: () => void;
  // false: pick from the list only, no typing
  isSearchable?: boolean;
};

const defaultProps: Partial<SelectProps> = {
  defaultValue: undefined,
  label: undefined,
  hasMaxMenuHeight: false,
  isSearchable: true,
};

export function Select(props: SelectProps): React.ReactElement {
  const {
    options,
    label,
    defaultValue,
    hasMaxMenuHeight,
    selectedValue,
    onChange,
    onMenuOpen,
    isSearchable,
  } = {
    ...defaultProps,
    ...props,
  };

  return (
    <S.Container>
      {label ? <S.Label>{label}</S.Label> : null}

      <S.Select
        maxMenuHeight={hasMaxMenuHeight ? 175 : undefined}
        menuPlacement="auto"
        onChange={onChange}
        onMenuOpen={onMenuOpen}
        isSearchable={isSearchable}
        value={selectedValue}
        options={options}
        defaultValue={defaultValue}
        styles={{
          // react-select's default blue focus ring and highlights, in the admin's black / gray
          option: (base, { isDisabled, isFocused, isSelected }) => {
            let backgroundColor = 'transparent';
            if (isSelected) backgroundColor = colors.black;
            else if (isFocused && !isDisabled) backgroundColor = OPTION_HOVER;

            return {
              ...base,
              backgroundColor,
              color: isSelected ? colors.white : base.color,
              cursor: isDisabled ? 'default' : 'pointer',
              ':active': {
                backgroundColor:
                  isDisabled || isSelected ? backgroundColor : colors.grayTertiary,
              },
            };
          },
          control: (base, { isFocused }) => ({
            ...base,
            height: 30,
            minHeight: 30,
            fontSize: 12,
            boxShadow: 'none',
            // the whole control opens the list, the chevron included
            cursor: 'pointer',
            borderColor: isFocused ? colors.black : base.borderColor,
            '&:hover': { borderColor: colors.black },
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            maxHeight: '100%',
          }),
        }}
      />
    </S.Container>
  );
}
